/**
 * Voice interaction hook and context (v0.10.2 — connectVoice API)
 *
 * Uses OmoteAvatarRef.connectVoice() which handles all orchestration:
 * mic → VAD → ASR → onResponse → PlaybackPipeline → lip sync
 *
 * This file only handles:
 * - React context (VoiceStateContext)
 * - Zustand transcript store wiring via config callbacks
 */

import { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { type LoadingProgress, type TranscriptResult, type ConversationalState, DEFAULT_MODEL_URLS, isMobile } from '@omote/core'
import { useTranscriptStore } from './useTranscriptStore'
import { useAvatarStore } from './useAvatarStore'
import { useAgentCore } from './useAgentCore'

// ============================================================================
// Types
// ============================================================================

export type VoiceState = 'idle' | 'loading' | 'ready' | 'listening' | 'thinking' | 'speaking' | 'error'

export type { LoadingProgress }

interface VoiceStateContextType {
  state: VoiceState
  error: string | null
  loadingProgress: LoadingProgress | null
  startConversation: () => Promise<void>
  endConversation: () => void
  loadModels: () => Promise<void>
}

const VoiceStateContext = createContext<VoiceStateContextType | null>(null)

export function useVoiceState() {
  const context = useContext(VoiceStateContext)
  if (!context) throw new Error('useVoiceState must be used within VoiceProvider')
  return context
}

// ============================================================================
// VoiceProvider
// ============================================================================

interface VoiceProviderProps {
  children: ReactNode
}

export function VoiceProvider({ children }: VoiceProviderProps) {
  const [state, setState] = useState<VoiceState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress | null>(null)

  const setTranscript = useTranscriptStore((s) => s.setTranscript)
  const setInterimTranscript = useTranscriptStore((s) => s.setInterimTranscript)
  const setTranscriptStatus = useTranscriptStore((s) => s.setTranscriptStatus)
  const resetTranscript = useTranscriptStore((s) => s.resetTranscript)

  const avatarRef = useAvatarStore((s) => s.avatarRef)
  const setErrorMsg = useCallback((msg: string) => setError(msg), [])
  const { onResponse, warmUp } = useAgentCore(setErrorMsg)

  const loadingRef = useRef(false)
  const connectedRef = useRef(false)
  const stateRef = useRef(state)
  stateRef.current = state

  // ---- loadModels ----
  const loadModels = useCallback(async () => {
    if (!avatarRef || loadingRef.current) return
    loadingRef.current = true
    setError(null)
    setState('loading')

    try {
      await avatarRef.connectVoice({
        mode: 'cloud',
        onResponse,

        // Config callbacks (B1) — wired before model loading starts
        onStateChange: (s: ConversationalState) => {
          if (s === 'listening' || s === 'thinking' || s === 'speaking') {
            setState(s)
          }
        },
        onLoadingProgress: setLoadingProgress,
        onError: (e: Error) => setError(e.message),
        onTranscriptEvent: (t: TranscriptResult) => {
          if (t.isFinal) {
            setTranscript((prev) => ({ ...prev, user: t.text }))
            setTranscriptStatus('final')
            setInterimTranscript('')
          } else {
            if (t.text.trim()) {
              setInterimTranscript(t.text.trim())
              setTranscriptStatus('interim')
            }
          }
        },
        onInterruption: () => {
          setInterimTranscript('')
          setTranscriptStatus('idle')
        },

        // ExpressionProfile
        profile: { mouth: 1.0, jaw: 1.0, brows: 0.6, eyes: 0.0, cheeks: 0.5, nose: 0.3, tongue: 0.5 },
        interruptionEnabled: true,
        identityIndex: 0,
        neutralTransitionEnabled: true,

        // SpeechListener config
        listener: {
          normalizeAudio: true,
          models: {
            senseVoice: { modelUrl: DEFAULT_MODEL_URLS.senseVoice, language: 'en' },
            vad: {
              modelUrl: DEFAULT_MODEL_URLS.sileroVad,
              threshold: isMobile() ? 0.65 : 0.5,
              preSpeechBufferChunks: 20,
            },
          },
        },
      })

      connectedRef.current = true
      setLoadingProgress(null)
      setState('ready')
      warmUp()  // Fire-and-forget: warm up AgentCore
    } catch (err) {
      loadingRef.current = false
      setError(err instanceof Error ? err.message : 'Failed to load models')
      setState('error')
      try { await avatarRef.disconnectVoice() } catch { /* ignore */ }
    }
  }, [avatarRef, onResponse, warmUp, setTranscript, setInterimTranscript, setTranscriptStatus])

  // ---- startConversation ----
  const startConversation = useCallback(async () => {
    if (!avatarRef || !connectedRef.current || stateRef.current !== 'ready') return
    resetTranscript()
    setError(null)
    await avatarRef.startListening()
  }, [avatarRef, resetTranscript])

  // ---- endConversation ----
  const endConversation = useCallback(() => {
    if (!avatarRef) return
    avatarRef.stopListening()
    avatarRef.stopSpeaking()
    avatarRef.reset()
    setInterimTranscript('')
    setTranscriptStatus('idle')
    setState('ready')
  }, [avatarRef, setInterimTranscript, setTranscriptStatus])

  // ---- Cleanup on unmount ----
  useEffect(() => {
    return () => {
      const ref = useAvatarStore.getState().avatarRef
      if (ref && connectedRef.current) {
        ref.disconnectVoice().catch(() => {})
      }
    }
  }, [])

  // ---- Context value ----
  const stateValue = useMemo(() => ({
    state, error, loadingProgress, startConversation, endConversation, loadModels,
  }), [state, error, loadingProgress, startConversation, endConversation, loadModels])

  return (
    <VoiceStateContext.Provider value={stateValue}>
      {children}
    </VoiceStateContext.Provider>
  )
}
